import { useEffect, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';

import { ImageAsset as TImageAsset, Maybe, BffBrandType, AvailableSlot } from '@/types/generated';
import { TRoundedCornersSize } from '@/types';
import { getMediaContainerStyles } from '@/components/ServiceLineHero/ServiceLineHero.styles';
import ImageAsset from '@/components/ImageAsset';
import { IImage } from '@/components/ImageAsset/ImageAsset';
import TAGButton from '@/components/TAGButton';
import TAGLink from '@/components/TAGLink';
import { useAppContext } from '@/context';

import {
	schedulingMessageStyles,
	schedulingTimesContentWrapperStyles,
	schedulingTimesImageStyles,
	schedulingTimesListStyles,
	schedulingTimesWrapperStyles,
	timeMessageStyles,
} from './SchedulingTimes.styles';
import { fetchTimes, formatDisplayTime } from './SchedulingTimes.helpers';

interface SchedulingTimesProps {
	roundedCornerSize?: Maybe<TRoundedCornersSize>;
	componentTheme: Theme;
	schedulingImageAsset?: Maybe<TImageAsset>;
	brand: BffBrandType;
	facilityId: string;
	scheduleAppointmentURL?: Maybe<string>;
}

export function SchedulingTimesList({
	slotsList = [],
	scheduleAppointmentURL = '',
}: Readonly<{
	slotsList: AvailableSlot[];
	scheduleAppointmentURL?: Maybe<string>;
	facilityId?: string;
}>) {
	return (
		<Box data-test-id="scheduling-times-list" sx={schedulingTimesListStyles}>
			{slotsList.map((slot) => (
				//'2024-03-20T10:00'
				<TAGButton
					href={`${scheduleAppointmentURL ?? ''}&timeSlot=${slot.date}T${slot.time}`}
					key={`scheduling-link-${slot.date}-${slot.time}`}
					variant="secondaryDefault"
				>
					{formatDisplayTime(slot.time)}
				</TAGButton>
			))}
		</Box>
	);
}

/**
 * SchedulingTimes component displays available appointment slots for a given facility and brand.
 * It fetches scheduling times from a backend service and shows either today's or tomorrow's availability.
 * If no slots are available, it displays a message and a button to see all times.
 *
 * @param {object} props - The component props.
 * @param {number} props.roundedCornerSize - The size of the rounded corners for the media container.
 * @param {Theme} props.componentTheme - The theme object used for styling the component.
 * @param {SchedulingImageAsset | null} [props.schedulingImageAsset=null] - Optional image asset to display alongside scheduling times.
 * @param {string} props.brand - The brand identifier used for fetching available slots.
 * @param {string} props.facilityId - The facility identifier used for fetching available slots.
 * @param {string} [props.scheduleAppointmentURL=''] - Optional URL to the full appointment scheduling page.
 *
 * @returns {JSX.Element} The rendered SchedulingTimes component.
 */
export default function SchedulingTimes({
	roundedCornerSize,
	componentTheme,
	schedulingImageAsset = null,
	brand,
	facilityId,
	scheduleAppointmentURL = '',
}: Readonly<SchedulingTimesProps>) {
	const { environment, config, appName, appVersion } = useAppContext();
	const url =
		environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;
	const containerStyles = getMediaContainerStyles({
		theme: componentTheme,
		mediaWithBorderRadius: true,
		imgDisplaysFirstOnDesktop: false,
		mediaRoundedCornerSize: roundedCornerSize,
	});

	const wrapperStyles = {
		...containerStyles,
		...schedulingTimesWrapperStyles,
	};

	const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
	const [hasTodaySlots, setHasTodaySlots] = useState<boolean>(false);
	const [schedulingDate, setSchedulingDate] = useState<string>('');

	useEffect(() => {
		const getTimes = async () => {
			const availableSlotsResponse = await fetchTimes({
				brand,
				facilityId,
				url,
				appName,
				appVersion,
			});
			setAvailableSlots(availableSlotsResponse.slots);
			setHasTodaySlots(availableSlotsResponse.isToday);
			setSchedulingDate(availableSlotsResponse.date);
		};
		void getTimes();
	}, [brand, facilityId, url, appName, appVersion]);

	const hasTimes = availableSlots?.length > 0;

	const schedulingDay = hasTodaySlots ? 'Today' : 'Tomorrow';

	const schedulingMessage = `Showing availability for `;
	const noSchedulingMessage = 'There are currently no appointments for today or tomorrow';

	return (
		<Box data-test-id="service_line_hero_scheduling" sx={wrapperStyles}>
			{schedulingImageAsset && (
				<Box data-test-id="scheduling-image-asset-wrapper" sx={schedulingTimesImageStyles}>
					<ImageAsset
						desktopImage={schedulingImageAsset.desktopImage as IImage}
						mobileImage={schedulingImageAsset.mobileImage as IImage}
						isBackgroundImage={false}
					/>
				</Box>
			)}
			<Box sx={schedulingTimesContentWrapperStyles}>
				<Typography variant="header1" mb="1.25rem">
					Schedule a visit
				</Typography>

				<Box data-test-id="scheduling-times-message" component="p" sx={{ color: 'text.secondary' }}>
					{hasTimes ? (
						<>
							<Typography variant="bodyMediumBook" sx={schedulingMessageStyles}>
								{schedulingMessage}
							</Typography>{' '}
							<Typography sx={schedulingMessageStyles} variant="bodyMediumBold">
								{schedulingDay}, {schedulingDate}
							</Typography>
						</>
					) : (
						<Typography data-test-id="no-scheduling-message" sx={{ mb: '1rem' }}>
							{noSchedulingMessage}
						</Typography>
					)}
				</Box>
				{hasTimes ? (
					<>
						<SchedulingTimesList
							slotsList={availableSlots}
							scheduleAppointmentURL={scheduleAppointmentURL}
							facilityId={facilityId}
						/>
						<Box component="p" style={timeMessageStyles}>
							<Typography
								variant="bodyMediumBook"
								sx={{
									color: componentTheme.palette.text.black,
								}}
							>
								Not seeing a time that works for you?
							</Typography>{' '}
							<TAGLink dataTestId="see-all-times-link" href={scheduleAppointmentURL ?? undefined}>
								See all times
							</TAGLink>
						</Box>
					</>
				) : (
					<Box id="no-times-available-button-section" sx={{ mt: '1.25rem' }}>
						<TAGButton
							dataTestId="see-all-times-button"
							href={scheduleAppointmentURL ?? ''}
							variant="primaryDefault"
							size="M"
							sx={{ mt: '1.25rem' }}
						>
							See all times
						</TAGButton>
					</Box>
				)}
			</Box>
		</Box>
	);
}
